import {
  type InfiniteData,
  type QueryKey,
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
} from "@tanstack/react-query";
import type { ApiResponse } from "@membentry/shared-types";
import type { AxiosInstance } from "axios";
import { getNextPageFromMeta } from "../utils/infinite-pagination";

export type InfiniteListOptions<TItem, TKey extends QueryKey> = Omit<
  UseInfiniteQueryOptions<
    ApiResponse<TItem[]>,
    Error,
    InfiniteData<ApiResponse<TItem[]>>,
    TKey,
    number
  >,
  "queryKey" | "queryFn" | "getNextPageParam" | "initialPageParam"
>;

export interface InfiniteListPageRequest {
  page: number;
  pageSize: number;
}

export interface InfiniteListSpec<TParams, TItem, TKey extends QueryKey> {
  pageSize: number | ((params: TParams) => number);
  buildQueryKey: (params: TParams, pageSize: number) => TKey;
  fetchPage: (
    client: AxiosInstance,
    params: TParams,
    request: InfiniteListPageRequest,
  ) => Promise<ApiResponse<TItem[]>>;
}

export function flattenInfiniteListPages<TItem>(
  data: InfiniteData<ApiResponse<TItem[]>> | undefined,
  keyOf?: (item: TItem) => string,
): TItem[] {
  const items = (data?.pages ?? []).flatMap((page) =>
    Array.isArray(page.payload) ? page.payload : [],
  );

  if (!keyOf) {
    return items;
  }

  const seen = new Set<string>();
  return items.filter((item) => {
    const key = keyOf(item);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

export function createInfiniteListQuery<TParams, TItem, TKey extends QueryKey>(
  spec: InfiniteListSpec<TParams, TItem, TKey>,
) {
  return function useInfiniteList(
    client: AxiosInstance,
    params: TParams,
    options?: InfiniteListOptions<TItem, TKey>,
  ) {
    const pageSize =
      typeof spec.pageSize === "function" ? spec.pageSize(params) : spec.pageSize;

    return useInfiniteQuery<
      ApiResponse<TItem[]>,
      Error,
      InfiniteData<ApiResponse<TItem[]>>,
      TKey,
      number
    >({
      queryKey: spec.buildQueryKey(params, pageSize),
      queryFn: ({ pageParam }) =>
        spec.fetchPage(client, params, { page: pageParam, pageSize }),
      getNextPageParam: (lastPage, allPages) =>
        getNextPageFromMeta(lastPage, allPages.length, pageSize),
      initialPageParam: 0,
      ...options,
    });
  };
}
